import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FYLKER } from "@/lib/fylker";
import type { SegmentFormState } from "@/lib/lead-segment";
import NaceCodePicker from "./NaceCodePicker";

interface SegmentFieldsProps {
  value: SegmentFormState;
  onChange: (next: SegmentFormState) => void;
  /** Agenter vurderer selskapene på innhold, ikke omsetning (krever regnskapsoppslag per selskap). */
  hideRevenue?: boolean;
}

/** Segmentfelter (bransje, sted, ansatte, omsetning). Brukes av listeskjemaet og, i CRM-4.2, agentskjemaet. */
export default function SegmentFields({ value, onChange, hideRevenue }: SegmentFieldsProps) {
  const set = (patch: Partial<SegmentFormState>) => onChange({ ...value, ...patch });
  const toggleFylke = (navn: string) =>
    set({ fylke: value.fylke.includes(navn) ? value.fylke.filter((f) => f !== navn) : [...value.fylke, navn] });

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <Label>Bransje (næringskode)</Label>
        <NaceCodePicker value={value.naeringskode} onChange={(naeringskode) => set({ naeringskode })} />
      </div>
      <fieldset className="space-y-1.5">
        <legend className="text-sm font-medium">Fylke</legend>
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
          {FYLKER.map((f) => (
            <label key={f.nummer} className="flex items-center gap-2 text-sm">
              <Checkbox checked={value.fylke.includes(f.navn)} onCheckedChange={() => toggleFylke(f.navn)} aria-label={f.navn} />
              {f.navn}
            </label>
          ))}
        </div>
      </fieldset>
      <div className="space-y-1.5">
        <Label htmlFor="seg-kommuner">Kommunenummer (valgfritt, kommaseparert)</Label>
        <Input id="seg-kommuner" value={value.kommunenummer} onChange={(e) => set({ kommunenummer: e.target.value })} placeholder="0301, 4601" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="seg-emp-min">Ansatte fra</Label>
          <Input id="seg-emp-min" inputMode="numeric" value={value.employeesMin} onChange={(e) => set({ employeesMin: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="seg-emp-max">Ansatte til</Label>
          <Input id="seg-emp-max" inputMode="numeric" value={value.employeesMax} onChange={(e) => set({ employeesMax: e.target.value })} />
        </div>
        {!hideRevenue && (
          <>
        <div className="space-y-1.5">
          <Label htmlFor="seg-rev-min">Omsetning fra (kr)</Label>
          <Input id="seg-rev-min" inputMode="numeric" value={value.revenueMin} onChange={(e) => set({ revenueMin: e.target.value })} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="seg-rev-max">Omsetning til (kr)</Label>
          <Input id="seg-rev-max" inputMode="numeric" value={value.revenueMax} onChange={(e) => set({ revenueMax: e.target.value })} />
        </div>
          </>
        )}
      </div>
      {!hideRevenue && (
        <p className="text-xs text-muted-foreground">
          Omsetning krever et regnskapsoppslag per selskap og gjelder maks 300 treff per søk.
        </p>
      )}
    </div>
  );
}
